import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle2, Clock, User, LogOut, RefreshCw } from 'lucide-react';
import sentinelLogo from './sentinel-logo.png';

const API_URL = '/api';

// Main App Component
export default function App() {
  const [user, setUser] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');                   
  const [sortBy, setSortBy] = useState('priority');        
  const [filterBy, setFilterBy] = useState('all');           
  const [searchTerm, setSearchTerm] = useState('');        

  // Define filter groups
  const filterGroups = {
  'coverage-issues': ['NO_COVERAGE', 'MEDICAID_INACTIVE', 'COVERAGE_NOT_BILLABLE', 'INVALID_INSURANCE'],
  'potential-risks': ['FIX_ID', 'STANDARDIZE_NAME', 'DUAL_ELIGIBLE_RISK', 'UNKNOWN_INSURANCE', 'VERIFY_COMMERCIAL'],
  'revenue-opportunities': ['ADD_MISSING_COVERAGE', 'UPDATE_MCO', 'UPDATE_TO_MANAGED_CARE', 'ADD_SECONDARY_MEDICAID', 'UPDATE_SECONDARY']
};

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Fetch tasks when user logs in
  useEffect(() => {
    if (user) {
      fetchTasks();
      fetchDashboard();
    }
  }, [user]);

// Display task count in browser tab
useEffect(() => {
  if (tasks.length > 0) {
    const pendingCount = tasks.filter(t => !t.completed).length;
    document.title = `(${pendingCount}) Sentinel Insurance Intelligence`;
  } else {
    document.title = 'Sentinel Insurance Intelligence';
  }
}, [tasks]);

  const checkAuth = async () => {
    try {
      const res = await fetch(`${API_URL}/health`, {
        credentials: 'include'
      });
      if (res.ok) {
        // Try to get tasks to see if we're authenticated
        fetchTasks();
      }
    } catch (err) {
      console.log('Not authenticated');
    }
  };

  const login = async (email, password) => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      const data = await res.json();
      
      if (res.ok) {
        setUser(data.user);
        setError('');
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Connection error - is the API running?');
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await fetch(`${API_URL}/logout`, {
      method: 'POST',
      credentials: 'include'
    });
    setUser(null);
    setTasks([]);
    setDashboard(null);
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/tasks`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setTasks(data);
        // If we don't have user info yet, set it from successful request
        if (!user) {
          setUser({ email: 'user@example.com', role: 'staff' }); // Basic user info
        }
      } else if (res.status === 401) {
        setUser(null);
      }
    } catch (err) {
      setError('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/dashboard`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error('Dashboard fetch failed:', err);
    }
  };

  const completeTask = async (taskId) => {
    try {
      const res = await fetch(`${API_URL}/tasks/${taskId}/complete`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (res.ok) {
        // Update local state
        setTasks(tasks.map(task => 
          task.id === taskId 
            ? { ...task, completed: true, completed_by: user.email, completed_at: new Date().toISOString() }
            : task
        ));
        // Refresh dashboard
        fetchDashboard();
      }
    } catch (err) {
      setError('Failed to complete task');
    }
  };

  const uncompleteTask = async (taskId) => {
    try {
      const res = await fetch(`${API_URL}/tasks/${taskId}/uncomplete`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (res.ok) {
        // Update local state - move task back to pending
        setTasks(tasks.map(task => 
          task.id === taskId 
            ? { ...task, completed: false, completed_by: null, completed_at: null }
            : task
        ));
        // Refresh dashboard counts
        fetchDashboard();
      }
    } catch (err) {
      setError('Failed to undo task');
    }
  };

  const refreshTasks = async () => {
    if (!user || user.role !== 'admin') {
      setError('Admin access required');
      return;
    }

    if (!window.confirm('This will refresh all tasks for today. Continue?')) {
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/refresh-tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tasks: [] }) // Empty for now - should come from Sentinel
      });

      if (res.ok) {
        await fetchTasks();
        await fetchDashboard();
        setError('');
      } else {
        setError('Failed to refresh tasks');
      }
    } catch (err) {
      setError('Failed to refresh tasks');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <LoginScreen onLogin={login} error={error} loading={loading} />;
  }

  const filterAndSortTasks = (tasks) => {
  // Make a copy of tasks array (like tasks.copy() in Python)
  let filtered = [...tasks];
  
  // SEARCH: If user typed something in search box
  if (searchTerm) {
    filtered = filtered.filter(task => 
      // Check if client name OR medicaid ID contains search term
      task.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.medicaid_id?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    // The ?. means "only call toLowerCase if medicaid_id exists" (like Python's if medicaid_id: medicaid_id.lower())
  }
  
  // FILTER: Apply category filter
  if (filterBy !== 'all') {
    if (filterGroups[filterBy]) {
      // Keep only tasks whose type is in the selected filter group
      filtered = filtered.filter(task => 
        filterGroups[filterBy].includes(task.task_type)
      );
    }
  }
  
  // SORT: Priority order for task types
  const taskPriority = {
    'NO_COVERAGE': 1,
    'MEDICAID_INACTIVE': 1,
    'COVERAGE_NOT_BILLABLE': 2,
    'INVALID_INSURANCE': 2,
    'FIX_ID': 3,
    'STANDARDIZE_NAME': 3,
    'DUAL_ELIGIBLE_RISK': 4,
    'UNKNOWN_INSURANCE': 5,
    'VERIFY_COMMERCIAL': 5,
    'ADD_MISSING_COVERAGE': 6,
    'UPDATE_MCO': 7,
    'UPDATE_TO_MANAGED_CARE': 7,
    'ADD_SECONDARY_MEDICAID': 8,
    'UPDATE_SECONDARY': 8
  };

  // Sort the filtered list
  filtered.sort((a, b) => {
    // This is like Python's sorted(filtered, key=lambda x: ...)
    switch(sortBy) {
      case 'priority':
        // Lower number = higher priority (1 comes before 10)
        return (taskPriority[a.task_type] || 99) - (taskPriority[b.task_type] || 99);
      case 'name':
        // Alphabetical by client name
        return a.client_name.localeCompare(b.client_name);
      case 'type':
        // Alphabetical by task type
        return a.task_type.localeCompare(b.task_type);
      case 'date':
        // Newest first
        return new Date(b.created_date) - new Date(a.created_date);
      default:
        return 0;
    }
  });
  
  return filtered;
};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold">Sentinel Dashboard</h1>
              {dashboard && (
                <div className="ml-8 flex items-center space-x-6 text-sm">
                  <span className="text-gray-500">
                    {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </span>
                  <span className="text-gray-700">
                    <span className="font-medium">{dashboard.today.completed}</span>
                    <span className="text-gray-500"> / {dashboard.today.total} completed</span>
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700 flex items-center">
                <User className="w-4 h-4 mr-1" />
                {user.email}
              </span>
              {user.role === 'admin' && (
                <button
                  onClick={refreshTasks}
                  disabled={loading}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh Tasks
                </button>
              )}
              <button
                onClick={logout}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
              >
                <LogOut className="w-4 h-4 mr-1" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center text-red-800">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <StatsCard
              title="Total Tasks"
              value={dashboard.today.total}
              icon={<Clock className="w-5 h-5" />}
              color="blue"
            />
            <StatsCard
              title="Completed"
              value={dashboard.today.completed}
              icon={<CheckCircle2 className="w-5 h-5" />}
              color="green"
            />
            <StatsCard
              title="Remaining"
              value={dashboard.today.remaining}
              icon={<AlertCircle className="w-5 h-5" />}
              color="amber"
            />
          </div>
        )}

        {/* Task List */}
        <div className="bg-white shadow rounded-lg">
          {/* Replace the old simple header with this new controls header */}
          <div className="px-6 py-4 border-b bg-gray-50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="text-lg font-medium">Tasks</h2>
              
              {/* Search Bar */}
              <div className="flex-1 max-w-md">
                <input
                  type="text"
                  placeholder="Search client name or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Quick Stats */}
              <div className="flex gap-3 mb-3 text-sm font-medium">
                <span className="px-2 py-1 bg-red-50 text-red-700 rounded">
                  🔴 {tasks.filter(t => !t.completed && filterGroups['coverage-issues'].includes(t.task_type)).length}
                </span>
                <span className="px-2 py-1 bg-yellow-50 text-yellow-700 rounded">
                  🟡 {tasks.filter(t => !t.completed && filterGroups['potential-risks'].includes(t.task_type)).length}
                </span>
                <span className="px-2 py-1 bg-green-50 text-green-700 rounded">
                  🟢 {tasks.filter(t => !t.completed && filterGroups['revenue-opportunities'].includes(t.task_type)).length}
                </span>
              </div>

              {/* Filter and Sort Dropdowns */}
              <div className="flex gap-2">
                <select 
                  value={filterBy} 
                  onChange={(e) => setFilterBy(e.target.value)}
                  className="text-sm border border-gray-300 rounded-md px-3 py-1.5"
                >
                  <option value="all">All Tasks</option>
                  <option value="coverage-issues">🔴 Coverage Issues</option>
                  <option value="potential-risks">🟡 Potential Risks</option>
                  <option value="revenue-opportunities">🟢 Revenue Opportunities</option>
                </select>
                
                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                  className="text-sm border border-gray-300 rounded-md px-3 py-1.5"
                >
                  <option value="priority">Sort: Priority</option>
                  <option value="name">Sort: Client Name</option>
                  <option value="type">Sort: Task Type</option>
                  <option value="date">Sort: Date</option>
                </select>
              </div>
            </div>
            
            {/* Show count when filtering */}
            {(searchTerm || filterBy !== 'all') && (
              <div className="mt-2 text-sm text-gray-600">
                Showing {filterAndSortTasks(tasks.filter(t => !t.completed)).length} of {tasks.filter(t => !t.completed).length} pending tasks
              </div>
            )}
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading tasks...</div>
          ) : tasks.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No tasks for today. Run Sentinel to generate tasks.
            </div>
          ) : (
            <TaskList tasks={filterAndSortTasks(tasks)} onComplete={completeTask} onUncomplete={uncompleteTask} />
          )}
        </div>
      </main>
    </div>
  );
}

// Login Component
function LoginScreen({ onLogin, error, loading }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin(email, password);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      onLogin(email, password);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img 
            src={sentinelLogo} 
            alt="Sentinel Insurance Intelligence" 
            className="h-45 mx-auto"
          />
        </div>
        
        <div className="bg-white shadow rounded-lg p-8">
          <div className="space-y-6">
            {error && (
              <div className="bg-red-50 text-red-800 p-3 rounded text-sm flex items-center">
                <AlertCircle className="w-4 h-4 mr-2" />
                {error}
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                required
              />
            </div>
            
            <button
              onClick={() => onLogin(email, password)}
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Task List Component
function TaskList({ tasks, onComplete, onUncomplete }) {  
  const pendingTasks = tasks.filter(t => !t.completed);
  const completedTasks = tasks.filter(t => t.completed);

  return (
    <div className="divide-y">
      {/* Pending Tasks */}
      {pendingTasks.map((task) => (
        <TaskItem 
          key={task.id} 
          task={task} 
          onComplete={onComplete} 
          onUncomplete={onUncomplete}  
        />
      ))}
      
      {/* Completed Tasks */}
      {completedTasks.length > 0 && (
        <>
          <div className="px-6 py-3 bg-gray-50 text-sm font-medium text-gray-500">
            Completed ({completedTasks.length})
          </div>
          {completedTasks.map((task) => (
            <TaskItem 
              key={task.id} 
              task={task} 
              onComplete={onComplete} 
              onUncomplete={onUncomplete}  
            />
          ))}
        </>
      )}
    </div>
  );
}

// Individual Task Component
function TaskItem({ task, onComplete, onUncomplete }) {
  const priorityColors = {
    high: 'text-red-600 bg-red-50',
    medium: 'text-amber-600 bg-amber-50',
    low: 'text-green-600 bg-green-50'
  };

  const typeColors = {
  // 🔴 Red - STOP THE BLEEDING (Revenue hemorrhaging)
  'NO_COVERAGE': 'bg-red-100 text-red-800',
  'MEDICAID_INACTIVE': 'bg-red-100 text-red-800',
  'COVERAGE_NOT_BILLABLE': 'bg-red-100 text-red-800',
  'INVALID_INSURANCE': 'bg-red-100 text-red-800',
  
  // 🟡 Yellow - POTENTIAL RISKS (Could be bleeding, needs investigation)
  'FIX_ID': 'bg-yellow-100 text-yellow-800',
  'STANDARDIZE_NAME': 'bg-yellow-100 text-yellow-800',
  'DUAL_ELIGIBLE_RISK': 'bg-yellow-100 text-yellow-800',
  'UNKNOWN_INSURANCE': 'bg-yellow-100 text-yellow-800',
  'VERIFY_COMMERCIAL': 'bg-yellow-100 text-yellow-800',
  
  // 🟢 Green - REVENUE OPPORTUNITIES (Capture more $)
  'ADD_MISSING_COVERAGE': 'bg-green-100 text-green-800',
  'UPDATE_MCO': 'bg-green-100 text-green-800',
  'UPDATE_TO_MANAGED_CARE': 'bg-green-100 text-green-800',
  'ADD_SECONDARY_MEDICAID': 'bg-green-100 text-green-800',
  'UPDATE_SECONDARY': 'bg-green-100 text-green-800'
};

  return (
    <div className={`px-6 py-4 ${task.completed ? 'bg-gray-50' : ''}`}>
      <div className="flex items-start">
        <div className="flex-1">
          <div className="flex items-center mb-1">
            <h3 className={`font-medium ${task.completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
              {task.client_name}
            </h3>
            {task.dob && (
              <span className="ml-2 text-sm text-gray-500">
                DOB: {task.dob}
              </span>
            )}
            {task.medicaid_id && (
              <span className="ml-2 text-sm text-gray-500">ID: {task.medicaid_id}</span>
            )}
          </div>
          
          <div className="flex items-center gap-3 mb-2">
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
              typeColors[task.task_type] || 'bg-gray-100 text-gray-800'
            }`}>
              {task.task_type}
            </span>
          </div>
          
          <p className={`text-sm ${task.completed ? 'text-gray-500' : 'text-gray-700'}`}>
            {task.task_description}
          </p>
          
          {task.completed && task.completed_by && (
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-gray-500">
                Completed by {task.completed_by} at {new Date(task.completed_at).toLocaleTimeString()}
              </p>
              <button
                onClick={() => onUncomplete(task.id)}  // USE onUncomplete HERE
                className="text-xs text-blue-600 hover:text-blue-800 ml-4"
              >
                Undo
              </button>
            </div>
          )}
        </div>
        
        {!task.completed && (
          <button
            onClick={() => onComplete(task.id)}
            className="ml-4 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Complete
          </button>
        )}
      </div>
    </div>
  );
}

// Stats Card Component
function StatsCard({ title, value, icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600'
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-3xl font-semibold mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}